import { Fragment, useState } from 'react';
import { ErrorText } from '../../components/ErrorText';
import { FloatingSelectField } from '../../components/FloatingSelectField';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SelectableOptionCard } from '../../components/SelectableOptionCard';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SubSelectCard } from '../../components/SubSelectCard';
import {
  EmptyTimeslots,
  Fields,
  OptionsSection,
  Panel,
  SectionError,
  SelectionCount,
} from './PackageInclusionChoicePanel.styles';
import type { PackageInclusionChoicePanelProps } from './PackageInclusionChoicePanel.types';
import { radius } from '../../theme/tokens';

export function PackageInclusionChoicePanel({
  options,
  selectedOptionIds,
  maximumSelections,
  selectionCountText,
  optionError,
  onOptionToggle,
  onDayChange,
  onTimeChange,
  labels = {},
}: PackageInclusionChoicePanelProps) {
  const {
    title = 'Inclusions.',
    subtitle = 'Choose one option, and your preferred time.',
    timeslotHeading = 'When would you like?',
    dayLabel = 'Select day',
    timeLabel = 'Select time',
    noTimeslots = 'No times available',
    noteDismissLabel,
  } = labels;

  const [dismissedNotes, setDismissedNotes] = useState<string[]>([]);

  // A one-option group swaps on click, so only cap groups that take several.
  const atMax =
    maximumSelections !== undefined &&
    maximumSelections > 1 &&
    selectedOptionIds.length >= maximumSelections;

  return (
    <Panel data-testid="package-inclusion-choice-panel">
      <SectionHeader title={title} subtitle={subtitle} />
      {selectionCountText && (
        <SelectionCount aria-live="polite">{selectionCountText}</SelectionCount>
      )}
      {optionError && (
        <SectionError role="alert" data-error="true" aria-invalid="true" tabIndex={-1}>
          <ErrorText>{optionError}</ErrorText>
        </SectionError>
      )}
      <OptionsSection>
        {options.map((option) => {
          const isSelected = selectedOptionIds.includes(option.id);
          const isDisabled = Boolean(option.disabled) || (atMax && !isSelected);

          return (
            <Fragment key={option.id}>
              <SelectableOptionCard
                label={option.label}
                isSelected={isSelected}
                disabled={isDisabled}
                imageUrl={option.imageUrl}
                imageAlt={option.imageAlt}
                onSelect={() => onOptionToggle(option.id)}
              />
              {isSelected && (
                <SubSelectCard heading={timeslotHeading}>
                  <Fields>
                    <FloatingSelectField
                      label={dayLabel}
                      options={option.dayOptions ?? []}
                      value={option.selectedDay}
                      onChange={(value) => onDayChange(option.id, value)}
                      error={option.dayError}
                      fixedValueLabel={option.fixedDayLabel}
                      aria-label={`${dayLabel} ${option.label}`}
                    />
                    {option.isLoading ? (
                      <SkeletonBlock height="72px" borderRadius={radius('card', 'radius-md')} />
                    ) : option.timeOptions.length ? (
                      <FloatingSelectField
                        label={timeLabel}
                        options={option.timeOptions}
                        value={option.selectedTime}
                        onChange={(value) => onTimeChange(option.id, value)}
                        error={option.timeError}
                        aria-label={`${timeLabel} ${option.label}`}
                      />
                    ) : (
                      <EmptyTimeslots
                        data-error={option.timeError ? 'true' : undefined}
                        aria-invalid={option.timeError ? true : undefined}
                        tabIndex={option.timeError ? -1 : undefined}
                      >
                        {option.timeError ? <ErrorText>{noTimeslots}</ErrorText> : noTimeslots}
                      </EmptyTimeslots>
                    )}
                  </Fields>
                  {option.noteText && !dismissedNotes.includes(option.id) && (
                    <InfoNote
                      text={option.noteText}
                      onDismiss={() => setDismissedNotes((current) => [...current, option.id])}
                      dismissLabel={noteDismissLabel}
                    />
                  )}
                </SubSelectCard>
              )}
            </Fragment>
          );
        })}
      </OptionsSection>
    </Panel>
  );
}
